import React, { useState, useEffect } from 'react';
import { Keyboard, RotateCcw, AlertCircle, Sparkles, Check, Trash2, Edit3, Settings2, ShieldAlert } from 'lucide-react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../lib/AuthContext';

export interface NexoraXHotkey {
  id: string;
  name: string;
  action: string;
  key: string;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
  description: string;
}

export const DEFAULT_HOTKEYS: NexoraXHotkey[] = [
  {
    id: 'toggle-marketplace',
    name: 'Toggle NexoraX Marketplace View',
    action: 'TOGGLE_MARKETPLACE_VIEW',
    key: 'm',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Instantly opens or closes the NexoraX Marketplace modal/view from any visual screen coordinate to accelerate navigation.'
  },
  {
    id: 'toggle-admin',
    name: 'Toggle Marketplace Admin Portal',
    action: 'TOGGLE_MARKETPLACE_ADMIN',
    key: 'a',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Enters or exits the inline product override editing console inside NexoraX Marketplace.'
  },
  {
    id: 'toggle-detail',
    name: 'Toggle Product Detailed View',
    action: 'TOGGLE_DETAIL_MODE',
    key: 'd',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Toggles between high-fidelity 3D projector detail mode and list presentation view.'
  },
  {
    id: 'add-cart',
    name: 'Trigger NexoraX Handshake',
    action: 'ADD_TO_LEDGER',
    key: 'h',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Increments Shopping Ledger cache (Cart) by one node for the currently highlighted asset.'
  },
  {
    id: 'clear-cart',
    name: 'Reset Shopping Ledger',
    action: 'CLEAR_LEDGER_CART',
    key: 'c',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Purges shopping ledger cache, resetting node count instantly.'
  },
  {
    id: 'cycle-geometry',
    name: 'Cycle 3D Wireframe Geometry',
    action: 'CYCLE_GEOMETRY',
    key: 'g',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Cycles active interactive PDP visualizer through concentric tech rings, cube, tetrahedron, torus, or sphere.'
  },
  {
    id: 'random-asset',
    name: 'Select Random Asset',
    action: 'SELECT_RANDOM_ASSET',
    key: 'r',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Selects a random product from the 50 high-fidelity premium assets list.'
  },
  {
    id: 'filter-all',
    name: 'Filter: ALL segments',
    action: 'FILTER_ALL',
    key: '0',
    ctrlKey: true,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Clears selective pipeline isolation, restoring view to all categories.'
  },
  {
    id: 'filter-infra',
    name: 'Filter: Infrastructure',
    action: 'FILTER_INFRASTRUCTURE',
    key: '1',
    ctrlKey: true,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Filters products list downstream to only Infrastructure assets.'
  },
  {
    id: 'filter-comms',
    name: 'Filter: Communications',
    action: 'FILTER_COMMUNICATIONS',
    key: '2',
    ctrlKey: true,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Filters products list downstream to only Communications assets.'
  },
  {
    id: 'filter-neural',
    name: 'Filter: Neural Core',
    action: 'FILTER_NEURAL',
    key: '3',
    ctrlKey: true,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Filters products list downstream to only Neural assets.'
  },
  {
    id: 'filter-workspace',
    name: 'Filter: Spatial Workspace',
    action: 'FILTER_WORKSPACE',
    key: '4',
    ctrlKey: true,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Filters products list downstream to only Workspace assets.'
  },
  {
    id: 'filter-security',
    name: 'Filter: Cryptographic Security',
    action: 'FILTER_SECURITY',
    key: '5',
    ctrlKey: true,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Filters products list downstream to only Security assets.'
  },
  {
    id: 'next-page',
    name: 'Navigate: Next Page Segment',
    action: 'NEXT_PAGE',
    key: 'ArrowRight',
    ctrlKey: false,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Advances list to the next page segment (e.g., viewing items 9-16, etc.).'
  },
  {
    id: 'prev-page',
    name: 'Navigate: Previous Page Segment',
    action: 'PREV_PAGE',
    key: 'ArrowLeft',
    ctrlKey: false,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    description: 'Recedes list to the previous page segment.'
  },
  {
    id: 'goto-spectacle',
    name: 'Navigate: Spectacle Command Node',
    action: 'GOTO_SPECTACLE_COMMAND',
    key: 's',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Instantly transitions the active view dashboard to the Spectacle Command Node.'
  },
  {
    id: 'toggle-help',
    name: 'Toggle Keyboard Matrix Guide',
    action: 'TOGGLE_HOTKEY_HELP',
    key: 'k',
    ctrlKey: false,
    altKey: true,
    shiftKey: false,
    metaKey: false,
    description: 'Instantly opens the Global Keyboard Shortcut Reference Directory.'
  }
];

export function formatHotkey(hk: { ctrlKey: boolean; altKey: boolean; shiftKey: boolean; metaKey: boolean; key: string }): string {
  const parts: string[] = [];
  if (hk.ctrlKey) parts.push('Ctrl');
  if (hk.altKey) parts.push('Alt');
  if (hk.shiftKey) parts.push('Shift');
  if (hk.metaKey) parts.push('Meta');
  
  let keyLabel = hk.key;
  if (keyLabel === ' ') keyLabel = 'Space';
  else if (keyLabel.length === 1) keyLabel = keyLabel.toUpperCase();
  else if (keyLabel === 'ArrowRight') keyLabel = 'Arrow Right';
  else if (keyLabel === 'ArrowLeft') keyLabel = 'Arrow Left';
  else if (keyLabel === 'ArrowUp') keyLabel = 'Arrow Up';
  else if (keyLabel === 'ArrowDown') keyLabel = 'Arrow Down';
  
  parts.push(keyLabel);
  return parts.join(' + ');
}

export function NexoraXHotkeysManager() {
  const { user } = useAuth();
  const [hotkeys, setHotkeys] = useState<NexoraXHotkey[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; mode: 'success' | 'error' | 'info' } | null>(null);

  // Load from database / cache
  useEffect(() => {
    async function fetchHotkeys() {
      try {
        // First try indexedDB/localStorage
        const cached = localStorage.getItem('nexorax_NexoraX_hotkeys');
        if (cached) {
          const parsed = JSON.parse(cached);
          const merged = [...parsed];
          DEFAULT_HOTKEYS.forEach(defHk => {
            if (!merged.find(h => h.id === defHk.id)) {
              merged.push(defHk);
            }
          });
          setHotkeys(merged);
          return;
        }

        // Then try remote Firestore
        if (user?.uid) {
          const docRef = doc(db, 'system_config', `hotkeys_${user.uid}`);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && data.hotkeys) {
              setHotkeys(data.hotkeys);
              localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(data.hotkeys));
              return;
            }
          }
        }
      } catch (err) {
        console.error("Hotkeys config read exception:", err);
      }
      
      // Fallback to factory defaults
      setHotkeys(JSON.parse(JSON.stringify(DEFAULT_HOTKEYS)));
    }
    
    fetchHotkeys();
  }, [user]);

  // Capture keystroke while recording
  useEffect(() => {
    if (!recordingId) return;

    function handleKeyDown(e: KeyboardEvent) {
      // Prevent browser shortcuts defaults like Alt+D focusing URL bar or Alt+A select all if recording
      e.preventDefault();
      
      const key = e.key;

      // Ignore modifier-only key presses
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(key)) return;

      setHotkeys(prev => prev.map(hk => {
        if (hk.id === recordingId) {
          return {
            ...hk,
            key: key,
            ctrlKey: e.ctrlKey,
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            metaKey: e.metaKey
          };
        }
        return hk;
      }));

      // Finish recording state
      setRecordingId(null);
      showFeedback(`Recorded Hotkey combination: ${formatHotkey({
        key,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey
      })}`, 'info');
    }

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [recordingId]);

  const showFeedback = (text: string, mode: 'success' | 'error' | 'info') => {
    setFeedback({ text, mode });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleSaveToState = async () => {
    setIsSaving(true);
    try {
      // Save locally
      localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(hotkeys));
      
      // Dispatch update event for global key listener
      window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: hotkeys }));

      // Save to Firestore permanently if authenticated
      if (user?.uid) {
        await setDoc(doc(db, 'system_config', `hotkeys_${user.uid}`), {
          uid: user.uid,
          updatedAt: new Date().toISOString(),
          hotkeys: hotkeys
        });
      }

      showFeedback("NEXORAX LEDGER SYNC: Hotkey custom matrix verified and bound.", "success");
    } catch (err) {
      console.error(err);
      showFeedback("HARDWARE LEDGER ERROR: Failed to write configuration to permanent cloud storage.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm("Restore custom hotkey allocations to factory defaults?")) {
      const reset = JSON.parse(JSON.stringify(DEFAULT_HOTKEYS));
      setHotkeys(reset);
      localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(reset));
      window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: reset }));
      showFeedback("NEXORAX RECOVERY: Factory default hotkeys restored.", "info");
    }
  };

  const handleSimulateAction = (action: string) => {
    window.dispatchEvent(new CustomEvent('nexorax-hotkey-action', { detail: { action } }));
    showFeedback(`HOTKEY SIMULATED: Triggered action [${action}] successfully.`, 'success');
  };

  return (
    <div className="bg-[#030712] border-2 border-cyan-500/20 rounded-2xl p-6 relative overflow-hidden text-left" style={{ boxShadow: '0 0 35px rgba(0, 255, 255, 0.05)' }}>
      {/* visual structural grid */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />

      {/* Header and IP protection declarations */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-900 pb-5 mb-6">
        <div>
          <span className="text-[10px] font-mono font-bold tracking-[0.3em] text-[#00ffff] uppercase block">
            System Sub-module 04 // Interactive Architecture
          </span>
          <h3 className="text-2xl font-sans font-black text-white uppercase tracking-tight mt-1 flex items-center gap-2">
            <Keyboard className="w-6 h-6 text-cyan-400" />
            NexoraX Custom Hotkeys Matrix
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed font-sans max-w-2xl mt-1.5">
            Command key allocations binding standard trigger parameters to client-side actions within the NexoraX Marketplace. Standard IP Rights reserved under NexoraX Digital Ltd SC887095 and UK Government Regulatory framework.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleResetToDefaults}
            className="px-3.5 py-1.5 bg-slate-950/80 border border-slate-800 text-slate-400 hover:text-white rounded hover:border-cyan-500/40 font-mono text-[9px] font-extrabold uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
            Restore Factory Defaults
          </button>
          
          <button
            onClick={handleSaveToState}
            disabled={isSaving}
            className="px-4 py-1.5 bg-emerald-950/40 border border-emerald-500/40 hover:border-emerald-400 text-emerald-300 rounded font-mono text-[9px] font-extrabold uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5 shadow-[0_0_15px_rgba(16,185,129,0.1)]"
          >
            {isSaving ? 'Compiling Registry...' : 'Save & Mount Config'}
          </button>
        </div>
      </div>

      {feedback && (
        <div className={`p-3.5 rounded-xl text-xs font-mono mb-6 border uppercase flex items-center gap-3 ${
          feedback.mode === 'success' ? 'bg-emerald-950/20 border-emerald-500/30 text-emerald-400' :
          feedback.mode === 'error' ? 'bg-rose-950/20 border-rose-500/30 text-rose-400' :
          'bg-cyan-950/20 border-cyan-500/30 text-cyan-400'
        }`}>
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="font-bold flex-1">{feedback.text}</span>
          <span className="text-[9px] font-bold text-slate-500">MAPPED ONCE</span>
        </div>
      )}

      {recordingId && (
        <div className="p-5 rounded-2xl bg-cyan-950/30 border-2 border-cyan-400 text-center animate-pulse mb-6 flex flex-col items-center justify-center gap-2">
          <Keyboard className="w-8 h-8 text-cyan-400 animate-bounce" />
          <h4 className="text-sm font-mono font-black text-cyan-300 uppercase">ACTIVE KEYBOARD SNIFFER ONLINE</h4>
          <p className="text-xs text-slate-400 max-w-lg">
            Press any keyboard key combination (e.g. <kbd className="bg-slate-950 text-white px-1.5 py-0.5 rounded font-mono border border-slate-800 text-[10px] mx-0.5">Alt + S</kbd>, <kbd className="bg-slate-950 text-white px-1.5 py-0.5 rounded font-mono border border-slate-800 text-[10px] mx-0.5">Shift + F5</kbd>, or a raw key) to map this workspace trigger. Press <kbd className="bg-slate-950 text-white px-1.5 py-0.5 rounded font-mono border border-slate-800 text-[10px] mx-0.5">Esc</kbd> to discard.
          </p>
          <button
            onClick={() => setRecordingId(null)}
            className="mt-2 text-[9px] p-1 px-3 border border-red-500/40 hover:border-red-500 text-red-400 rounded bg-slate-950 font-mono tracking-widest uppercase cursor-pointer"
          >
            Cancel Sniffer
          </button>
        </div>
      )}

      {/* Grid view showing custom bindings of NexoraX actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hotkeys.map((hk) => {
          const isSelected = recordingId === hk.id;
          const keyComboStr = formatHotkey(hk);

          return (
            <div
              key={hk.id}
              className={`border rounded-xl p-4 flex flex-col justify-between gap-3 text-xs font-mono relative transition-all group overflow-hidden ${
                isSelected
                  ? 'border-cyan-400 bg-cyan-950/15 ring-2 ring-cyan-500/30'
                  : 'border-slate-850 bg-slate-950/40 hover:border-slate-800 hover:bg-slate-950/60'
              }`}
            >
              {/* Highlight background subtle hue */}
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-slate-900/40 to-transparent pointer-events-none group-hover:bg-slate-900/25 transition-colors" />

              <div className="space-y-1">
                <span className="text-[10px] font-sans font-extrabold tracking-tight text-slate-300 block uppercase">
                  {hk.name}
                </span>
                <span className="text-[9px] text-[#00ffff]/60 uppercase tracking-widest block font-bold">
                  ACTION CODE: {hk.action}
                </span>
                <p className="text-[10px] text-slate-500 font-sans leading-relaxed pt-1">
                  {hk.description}
                </p>
              </div>

              <div className="flex items-center justify-between gap-1 border-t border-slate-900/60 pt-3 mt-1.5">
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-slate-500">MAPPED TO:</span>
                  <kbd className="px-2 py-0.5 bg-slate-950 border border-slate-800 rounded text-cyan-400 font-bold text-[10.5px] shadow-xs">
                    {keyComboStr}
                  </kbd>
                </div>

                <div className="flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setRecordingId(hk.id)}
                    className="p-1 px-2 border border-slate-800 hover:border-cyan-500/40 text-slate-400 hover:text-cyan-400 rounded text-[9px] uppercase font-bold tracking-widest cursor-pointer bg-slate-950/60 flex items-center gap-1"
                    title="Record Keystroke"
                  >
                    <Edit3 className="w-3 h-3" />
                    Record
                  </button>

                  <button
                    onClick={() => handleSimulateAction(hk.action)}
                    className="p-1 px-2 border border-emerald-900/40 hover:border-emerald-500/50 text-slate-400 hover:text-emerald-400 rounded text-[9px] uppercase font-bold tracking-widest cursor-pointer bg-slate-950/60 flex items-center gap-1"
                    title="Fire NexoraX test action"
                  >
                    Test Action
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 border-t border-slate-900 pt-5 flex items-center gap-3">
        <div className="p-2 bg-yellow-950/10 border border-yellow-500/20 text-yellow-500 rounded-lg shrink-0">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <div className="text-left font-sans text-slate-500 text-[11px] leading-relaxed">
          <span className="font-bold text-slate-400 uppercase tracking-wider block">Important Usage Sandbox Parameter:</span>
          Keyboard Hotkeys are dynamically routed inside the client-side single-view router. Keystroke detection is automatically suspended when administrative fields, dynamic chat inputs, or textareas inside GDrive replication folders are focused to prevent duplicate trigger conflicts.
        </div>
      </div>
    </div>
  );
}
